/**
 * @fileoverview Events Tab Section component.
 * @summary Displays events tab with filters and event list.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/EventsTabSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import { SingerEventCard } from './SingerEventCard.js'

interface SingerEvent {
  id: string
  name: string
  organizerName: string
  date: string
  status: 'draft' | 'active' | 'completed' | 'cancelled'
  songsCount: number
}

interface EventsTabSectionProps {
  events: SingerEvent[]
  eventFilter: 'all' | 'active' | 'past' | 'inactive'
  onFilterChange: (filter: 'all' | 'active' | 'past' | 'inactive') => void
  getStatusColor: (status: string) => string
  formatDate: (dateString: string) => string
  onJoinEvent: (eventId: string) => void
}

/**
 * @summary Events tab section component.
 * @param {EventsTabSectionProps} props - Component props.
 * @returns {React.ReactElement} Events tab section.
 */
export function EventsTabSection({
  events,
  eventFilter,
  onFilterChange,
  getStatusColor,
  formatDate,
  onJoinEvent,
}: EventsTabSectionProps) {
  const { t } = useTranslation()

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2 justify-evenly">
        {(['all', 'active', 'past', 'inactive'] as const).map((filter) => (
          <button
            key={filter}
            className={`btn btn-sm whitespace-nowrap ${
              eventFilter === filter ? 'btn-primary' : 'btn-secondary'
            }`}
            onClick={() => onFilterChange(filter)}
          >
            {t(`singer.${filter === 'inactive' ? 'inactiveEvents' : filter === 'past' ? 'pastEvents' : filter === 'active' ? 'activeEvents' : 'allEvents'}`)}
          </button>
        ))}
      </div>

      {/* Events List */}
      {events.length === 0 ? (
        <div className="card bg-base-100 dark:bg-base-800 text-center py-12">
          <p className="text-base-600 dark:text-base-400">{t('singer.noEvents')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <SingerEventCard
              key={event.id}
              event={event}
              getStatusColor={getStatusColor}
              formatDate={formatDate}
              onJoin={onJoinEvent}
            />
          ))}
        </div>
      )}
    </div>
  )
}

